import { useState, useRef, useEffect } from "react";
import { PhoneIcon, CheckIcon } from "@heroicons/react/24/outline";
import { IMultiSiteOwnerRestaurant } from "../../interfaces/multiSiteOwner";

interface RestaurantTableProps {
    restaurants: IMultiSiteOwnerRestaurant[];
}

export default function RestaurantTable({ restaurants }: RestaurantTableProps) {
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [phonePopupId, setPhonePopupId] = useState<string | null>(null);
    const [copied, setCopied] = useState<string | null>(null);
    const phoneRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (phoneRef.current && !phoneRef.current.contains(event.target as Node)) {
                setPhonePopupId(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        setCopied(text);
        setTimeout(() => setCopied(null), 2000);
    };

    const toggleSelection = (id: string) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    const toggleAll = () => {
        if (selectedIds.length === restaurants.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(restaurants.map((r) => r.id));
        }
    };

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full">
                <thead className="bg-white border-t border-gray-200">
                    <tr>
                        <th className="px-6 py-4 align-middle">
                            <input
                                type="checkbox"
                                checked={selectedIds.length === restaurants.length && restaurants.length > 0}
                                onChange={toggleAll}
                                className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black cursor-pointer"
                            />
                        </th>
                        <th className="px-6 py-4 align-middle text-left">
                            <span className="text-sm font-medium text-gray-600">Nom</span>
                        </th>
                        <th className="px-6 py-4 align-middle text-left">
                            <span className="text-sm font-medium text-gray-600">Responsable</span>
                        </th>
                        <th className="px-6 py-4 align-middle text-left">
                            <span className="text-sm font-medium text-gray-600">Adresse</span>
                        </th>
                        <th className="px-6 py-4 align-middle text-left">
                            <span className="text-sm font-medium text-gray-600">Ville</span>
                        </th>
                        <th className="px-6 py-4 align-middle text-left">
                            <span className="text-sm font-medium text-gray-600">Contact</span>
                        </th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {restaurants.map((restaurant) => (
                        <tr
                            key={restaurant.id}
                            className="hover:bg-gray-50 transition-colors group"
                        >
                            <td className="px-6 py-4 align-middle">
                                <input
                                    type="checkbox"
                                    checked={selectedIds.includes(restaurant.id)}
                                    onChange={() => toggleSelection(restaurant.id)}
                                    className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black cursor-pointer"
                                />
                            </td>
                            <td className="px-6 py-4 align-middle">
                                <div className="text-sm font-medium text-gray-900">{restaurant.name}</div>
                                <div className="text-xs text-gray-500">{restaurant.type}</div>
                            </td>
                            <td className="px-6 py-4 align-middle">
                                <div className="text-sm text-gray-900">{restaurant.manager}</div>
                            </td>
                            <td className="px-6 py-4 align-middle">
                                <div className="text-sm text-gray-900">{restaurant.address}</div>
                            </td>
                            <td className="px-6 py-4 align-middle">
                                <div className="text-sm text-gray-900">{restaurant.city}</div>
                                <div className="text-xs text-gray-500">{restaurant.postalCode}</div>
                            </td>
                            <td className="px-6 py-4 align-middle">
                                <div className="relative" ref={phoneRef}>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setPhonePopupId(
                                                phonePopupId === restaurant.id ? null : restaurant.id
                                            );
                                        }}
                                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                    >
                                        <PhoneIcon className="h-4 w-4" />
                                    </button>

                                    {phonePopupId === restaurant.id && (
                                        <div className="absolute z-10 left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-[200px]">
                                            <p className="text-xs text-gray-500 mb-1">Téléphone</p>
                                            <div className="flex items-center justify-between gap-2">
                                                <p className="text-sm font-medium text-gray-900">
                                                    {restaurant.phone}
                                                </p>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        copyToClipboard(restaurant.phone);
                                                    }}
                                                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                                                >
                                                    {copied === restaurant.phone ? (
                                                        <CheckIcon className="h-4 w-4 text-green-600" />
                                                    ) : (
                                                        <svg
                                                            className="h-4 w-4 text-gray-600"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            stroke="currentColor"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                                            />
                                                        </svg>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
